import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Pause, Gift, Heart } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Background images mapping
const GIFT_BACKGROUNDS = {
  sunrise: {
    url: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800&q=80",
    overlay: "rgba(180, 120, 80, 0.4)"
  },
  flowers: {
    url: "https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=800&q=80",
    overlay: "rgba(150, 100, 120, 0.4)"
  },
  ocean: {
    url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80",
    overlay: "rgba(60, 100, 140, 0.4)"
  },
  forest: {
    url: "https://images.unsplash.com/photo-1448375240586-882707db888b?w=800&q=80",
    overlay: "rgba(50, 80, 50, 0.5)"
  },
  stars: {
    url: "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=800&q=80",
    overlay: "rgba(30, 40, 80, 0.5)"
  }
};

export const GiftViewPage = () => {
  const { giftId } = useParams();
  const audioRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gift, setGift] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);

  useEffect(() => {
    fetchGift();
  }, [giftId]);

  const fetchGift = async () => {
    try {
      const res = await fetch(`${API_URL}/api/voice-studio/gifts/${giftId}`);
      if (!res.ok) {
        throw new Error("Gift not found");
      }
      const data = await res.json();
      setGift(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePlayback = async () => {
    if (!audioRef.current || !gift?.audio_url) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        audioRef.current.src = gift.audio_url;
        audioRef.current.load();
        await audioRef.current.play();
        setIsPlaying(true);
        setHasPlayed(true);
      } catch (err) {
        console.log("Playback error:", err.message);
      }
    }
  };

  const background = GIFT_BACKGROUNDS[gift?.background_id] || GIFT_BACKGROUNDS.forest;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1F2937] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !gift) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#D4A373] to-[#A67C52] flex items-center justify-center p-6">
        <div className="text-center text-white">
          <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Gift size={40} className="text-white/50" />
          </div>
          <h1 className="font-heading text-2xl mb-2">Gift Not Found</h1>
          <p className="text-white/60">This gift may have expired or the link is invalid.</p>
          <a 
            href="/" 
            className="inline-block mt-6 px-6 py-3 bg-white/20 rounded-full text-white hover:bg-white/30 transition-colors"
          >
            Visit Reclaim
          </a>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{
        backgroundImage: `url(${background.url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div 
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: background.overlay }}
      >
        {/* Header */}
        <div className="p-6 text-center">
          <p className="text-white/60 text-sm tracking-widest uppercase">A Gift For You</p>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-sm"
          >
            {/* Gift Card */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 text-center">
              {/* Gift Icon */}
              <motion.div
                animate={{ 
                  scale: isPlaying ? [1, 1.1, 1] : 1,
                }}
                transition={{ 
                  repeat: isPlaying ? Infinity : 0, 
                  duration: 2 
                }}
                className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                {hasPlayed ? (
                  <Heart size={36} className="text-white" fill={hasPlayed ? "white" : "none"} />
                ) : (
                  <Gift size={36} className="text-white" />
                )}
              </motion.div>

              {/* Recipient */}
              {gift.recipient_name && (
                <p className="text-white/70 text-sm mb-2">
                  Dear {gift.recipient_name},
                </p>
              )}

              {/* Message */}
              <p className="font-heading text-2xl text-white italic leading-relaxed mb-8">
                "{gift.message}"
              </p>

              {/* Play Button */}
              <button
                onClick={togglePlayback}
                className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto shadow-2xl hover:scale-105 transition-transform"
                data-testid="play-gift-btn"
              >
                {isPlaying ? (
                  <Pause size={36} className="text-[#D4A373]" />
                ) : (
                  <Play size={36} className="text-[#D4A373] ml-1" />
                )}
              </button>

              <p className="text-white/60 text-sm mt-4">
                {isPlaying ? "Playing..." : hasPlayed ? "Play again" : "Tap to hear their voice"}
              </p>

              {/* Sender */}
              {gift.sender_name && (
                <p className="text-white/50 text-sm mt-6">
                  From {gift.sender_name}
                </p>
              )}
            </div>
          </motion.div>
        </div>

        {/* Footer CTA */}
        <div className="p-6 text-center border-t border-white/10">
          <p className="text-white/60 text-sm mb-3">Send your own voice gift</p>
          <a 
            href="/"
            className="inline-block px-6 py-3 bg-white text-[#D4A373] font-medium rounded-full hover:bg-white/90 transition-colors"
          >
            Try Reclaim Free
          </a>
        </div>

        {/* Hidden audio element */}
        <audio 
          ref={audioRef} 
          onEnded={() => setIsPlaying(false)}
          preload="none"
        />
      </div>
    </div>
  );
};

export default GiftViewPage;
